import { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Button } from "./ui/button";
import { ChevronDown, Check, User } from "lucide-react";

/**
 * Searchable member picker. Wraps shadcn's Command + Popover so admins and
 * voters can filter by name/email as they type. `options` is an array of
 * {id, name, email?}; onChange receives the picked option or null.
 *
 * Optional `allowFreeText` renders a "Use as typed → <query>" row at the
 * bottom so the voter's write-in field can accept an arbitrary name too.
 */
export default function MemberPicker({
    options = [],
    value,
    onChange,
    placeholder = "Search members…",
    testid = "member-picker",
    allowFreeText = false,
    freeTextLabel = "Use this exact name",
    disabled = false,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const selected = useMemo(
        () => (value && typeof value === "object" ? value : options.find((o) => o.id === value)) || null,
        [value, options],
    );
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options.slice(0, 100);
        const hits = options.filter((o) =>
            (o.name || "").toLowerCase().includes(q) || (o.email || "").toLowerCase().includes(q),
        );
        return hits.slice(0, 100);
    }, [options, query]);

    const displayLabel = selected?.name || (typeof value === "string" ? value : "");

    return (
        <Popover open={open && !disabled} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    disabled={disabled}
                    className="w-full justify-between rounded-xl font-normal"
                    data-testid={testid}
                >
                    <span className="flex items-center gap-2 min-w-0">
                        <User className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="truncate">{displayLabel || placeholder}</span>
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command shouldFilter={false}>
                    <CommandInput
                        value={query}
                        onValueChange={setQuery}
                        placeholder="Type a name or email…"
                        data-testid={`${testid}-input`}
                    />
                    <CommandList>
                        <CommandEmpty>No matches.</CommandEmpty>
                        <CommandGroup>
                            {filtered.map((o) => (
                                <CommandItem
                                    key={o.id}
                                    value={`${o.name} ${o.email || ""}`}
                                    onSelect={() => { onChange(o); setOpen(false); setQuery(""); }}
                                    data-testid={`${testid}-option-${o.id}`}
                                >
                                    <Check className={`mr-2 h-4 w-4 ${selected?.id === o.id ? "opacity-100" : "opacity-0"}`} />
                                    <div className="min-w-0">
                                        <div className="font-medium truncate">{o.name}</div>
                                        {o.email && <div className="text-xs text-muted-foreground truncate">{o.email}</div>}
                                    </div>
                                </CommandItem>
                            ))}
                            {allowFreeText && query.trim() && !filtered.some((o) => (o.name || "").toLowerCase() === query.trim().toLowerCase()) && (
                                <CommandItem
                                    value={`__freetext_${query}`}
                                    onSelect={() => { onChange({ id: null, name: query.trim() }); setOpen(false); }}
                                    data-testid={`${testid}-freetext`}
                                >
                                    <Check className="mr-2 h-4 w-4 opacity-0" />
                                    <span className="italic">{freeTextLabel}: <b className="not-italic">{query.trim()}</b></span>
                                </CommandItem>
                            )}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
